/**
 * The three-part access rule, from plan.md:
 *
 *   canAccess(asset, subtype, patronTier):
 *     asset.status === 'published'
 *     && patronTier.amountCents >= tierById(asset.minTier).amountCents
 *     && matrix[matrixKeyFor(asset.assetType, subtype)][patronTier.id] === true
 *
 * **This copy is not the gate.** It decides what the UI draws - which download
 * buttons are live, what the library shows. The enforcing copy is
 * `AccessService.canAccess` on the server (task 17), and §11.4 is explicit:
 * "the UI state is a convenience, not the gate." If these two ever disagree the
 * server wins, and the bug is here.
 */

import { matrixKeyFor } from "./types";
import type { Asset, Creator, Tier } from "./types";

export function canAccess(
  asset: Pick<Asset, "status" | "assetType" | "minTier">,
  slotId: string,
  patronTier: Tier | null,
  creator: Pick<Creator, "tiers" | "matrix">
): boolean {
  // Drafts are invisible at every tier, including the owner's patron view.
  if (asset.status !== "published") return false;
  if (!patronTier) return false;

  const minTier = creator.tiers.find((t) => t.id === asset.minTier);
  if (!minTier) return false;
  // "and above" is defined by price, never by display order (D-5).
  if (patronTier.amountCents < minTier.amountCents) return false;

  // Keyed on the (type, slot) pair: a tier can hold the PDF and not the
  // MusicXML on the same asset (D-7).
  const row = creator.matrix[matrixKeyFor(asset.assetType, slotId)];
  return row?.[patronTier.id] === true;
}

/**
 * Whether an asset belongs in the library at all: published, and the tier can
 * reach at least one of its slots.
 */
export function isVisible(
  asset: Pick<Asset, "status" | "assetType" | "minTier" | "files">,
  patronTier: Tier | null,
  creator: Pick<Creator, "tiers" | "matrix">
): boolean {
  if (asset.status !== "published") return false;
  return Object.keys(asset.files).some((slotId) =>
    canAccess(asset, slotId, patronTier, creator)
  );
}
